import {
  Authenticator,
  Button,
  Flex,
  Grid,
  Heading,
  Text,
  View,
} from "@aws-amplify/ui-react";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ArrowLeftIcon from "../../../components/icons/ArrowLeftIcon";
import { useHistoryContext } from "../hooks/useHistoryContext";
import { useGameResults } from "../hooks/useGameResults";

import ResultsOverview from "./ResultsOverview";
import { useTeamResultAnalytics } from "../hooks/useTeamResultAnalytics";
import ChallengingQuestions from "./ChallengingQuestions";
import ResultsTable from "./ResultsTable";
import GradeSummary from "./GradeSummary";
import { useQueries } from "@tanstack/react-query";
import { getTeamPlayers } from "../../game/services/getTeamPlayers";
import { Player } from "../../game/Types/GameTypes_dep";
import Leaderboard from "./Leaderboard";
import { PageIndex, PageTitle } from "../../../util/SEO";

export default function LobbyResultsPage() {
  PageTitle("Game Results | Gibbly");
  PageIndex("noindex");
  const { lobbyId } = useParams();
  const navigate = useNavigate();

  const { teamsMap, lobbies } = useHistoryContext();

  const lobby = lobbies.find((lobby) => lobby.id === lobbyId);

  // const {} = useQuery({
  //   queryKey: ["lobbies", lobbyId],
  //   queryFn: async () => {
  //     try {
  //       const resp = await RestAPI.get(`/lobbies/history/${lobbyId}`);
  //       const lobby = (await resp.json()) as any as Lobby;
  //       return lobby;
  //     } catch {
  //       navigate("/dashboard/history");
  //       throw new Error("Lobby not found");
  //     }
  //   },
  // });

  const teams = useMemo(() => {
    if (!lobby) return [];
    const teams = teamsMap[lobby.id] || [];
    return teams;
  }, [teamsMap, lobby?.id]);

  const playerQueries = useQueries({
    queries: teams.map((team) => ({
      queryKey: ["lobbies", lobbyId, "teams", team.id, "players"],
      queryFn: async () => {
        return getTeamPlayers(team.id);
      },
    })),
  });

  const players = playerQueries
    .flatMap((query) => query.data)
    .filter((p) => p !== undefined) as Player[];

  const analytics = useTeamResultAnalytics(teams, players);

  const gameResults = useGameResults(
    teams.map((team) => team.gameData),
    lobby?.quiz
  );

  return (
    <>
      <Authenticator>
        <Flex
          width={"100%"}
          direction={"column"}
          padding={{ base: "0", medium: "medium" }}
          paddingTop={"large"}
          gap={{ base: "medium", medium: "xxxs" }}
        >
          <Flex
            justifyContent={{ base: "center", small: "space-between" }}
            direction={{ base: "column", small: "row" }}
          >
            <Flex>
              <Button
                variation="link"
                size="large"
                onClick={() => navigate(`/dashboard/history`)}
              >
                <ArrowLeftIcon />
              </Button>
            </Flex>
            <Flex textAlign={"center"} justifyContent={"center"}></Flex>
          </Flex>

          <Heading id="pageTitle">Game Results</Heading>

          <Text paddingBottom={"large"}>{lobby?.quiz.title}</Text>

          <View width={"100%"} grow={1}>
            <Flex direction={"column"} rowGap={"small"} paddingBottom={"xxl"}>
              <Grid
                templateColumns={{ base: "1fr", medium: "1fr 1fr" }}
                gap={"medium"}
              >
                {gameResults.completed && (
                  <ResultsOverview analytics={analytics} />
                )}

                <ChallengingQuestions
                  answerResults={gameResults.allAnswerResults}
                  quiz={lobby?.quiz}
                />
              </Grid>
              {gameResults.completed && (
                <GradeSummary analytics={analytics} players={players} />
              )}
              {lobby && <ResultsTable quiz={lobby.quiz} teams={teams} />}
              {gameResults.all && (
                <Leaderboard teams={teams} players={players} />
              )}
            </Flex>
          </View>
        </Flex>
      </Authenticator>
    </>
  );
}
